// CrewApp.jsx
// Mobile-first crew management component — replaces InstallerSchedule
// Features:
//   - Today's job assignments with full scope of work
//   - Photo upload with Before/During/After tagging
//   - Material shortage flagging (instant SMS to office)
//   - Progress notes with timestamps
//   - Time clock with GPS (TOGGLABLE — off by default)
//   - Weekly schedule view + calendar
//
// Import and swap in main CRM: case "schedule": return <CrewApp ... />

import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';

const BASE_URL = process.env.REACT_APP_BASE_URL || window.location.origin;

const CrewApp = ({ jobs, setJobs, estimates, events, currentUser, addNotification, loadJobPhotos, photoLoading }) => {
  // ── Core state ──
  const [activeJob, setActiveJob] = useState(null);
  const [tab, setTab] = useState("active");
  const [noteText, setNoteText] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef();

  // ── Time clock state (togglable feature) ──
  const [timeClockEnabled, setTimeClockEnabled] = useState(() => {
    try { return localStorage.getItem('ttp_timeclock_enabled') === 'true'; } catch { return false; }
  });
  const [clockStatus, setClockStatus] = useState(null);
  const [clockLoading, setClockLoading] = useState(false);

  // ── Material flag state ──
  const [flagModal, setFlagModal] = useState(false);
  const [flagForm, setFlagForm] = useState({ material: '', category: 'Other', supplier: '', qty: 1, unit: 'ea', price: '', quantity: '', urgency: 'normal', notes: '', receiptRef: '', reimb: false });
  const [flagSending, setFlagSending] = useState(false);
  const [openFlags, setOpenFlags] = useState([]);

  const FLAG_CATEGORIES = ["Tile","Adhesive & Grout","Waterproofing","Subfloor & Prep","Lumber & Framing","Hardware & Fasteners","Fixtures & Plumbing","Cabinets & Counters","Equipment Rental","Other"];
  const FLAG_SUPPLIERS = ["The Tile Shop","Home Depot","Lowe's","Floor & Decor","Local Vendor","Other"];
  const FLAG_UNITS = ["ea","sqft","bag","box","roll","gal","sheet","day","job"];

  // ── Photo tag state ──
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [photoTagModal, setPhotoTagModal] = useState(false);

  const PHOTO_TAGS = [
    { id: 'Before', label: 'Before', color: '#3b5bdb', desc: 'Existing conditions' },
    { id: 'During', label: 'During', color: '#946c00', desc: 'Work in progress' },
    { id: 'After', label: 'After', color: '#1a6b2a', desc: 'Final result — posted to Google' },
    { id: 'Substrate', label: 'Substrate', color: '#5a5248', desc: 'Prep before tile' },
    { id: 'Other', label: 'Other', color: '#8c7b6e', desc: 'Reference photos' },
  ];

  // ── Derived data ──
  const myName = currentUser?.name?.toLowerCase() || "";
  const isManager = currentUser?.role === 'owner' || currentUser?.role === 'admin';
  const myJobs = isManager
    ? [...jobs].sort((a, b) => new Date(a.startDate || "2099") - new Date(b.startDate || "2099"))
    : jobs.filter(j => {
        const crew = (j.crewName || j.crew || "").toLowerCase();
        return crew === myName || crew.includes(myName);
      }).sort((a, b) => new Date(a.startDate || "2099") - new Date(b.startDate || "2099"));

  const todayLocal = new Date().toISOString().slice(0, 10);
  const active = myJobs.filter(j => j.status === "In Progress");
  const upcoming = myJobs.filter(j => j.status === "Scheduled" && (j.startDate || "9999") >= todayLocal);
  const completed = myJobs.filter(j => j.status === "Completed");
  const weekFromNow = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const thisWeek = myJobs.filter(j => { const d = j.startDate || ""; return d >= todayLocal && d <= weekFromNow && j.status !== "Completed"; });

  const getEstimate = (estId) => (estimates || []).find(e => e.id === estId) || null;

  // ── Time clock effects ──
  useEffect(() => {
    if (timeClockEnabled) fetchClockStatus();
  }, [timeClockEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try { localStorage.setItem('ttp_timeclock_enabled', timeClockEnabled ? 'true' : 'false'); } catch {}
  }, [timeClockEnabled]);

  // ── Fetch material flags ──
  useEffect(() => {
    fetchOpenFlags();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Time clock functions ──
  const fetchClockStatus = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/crew/time-clock?userId=${currentUser?.id}`);
      const data = await res.json();
      setClockStatus(data);
    } catch (e) { console.log('Clock status error:', e.message); }
  };

  const getGPS = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ lat: null, lng: null });
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve({ lat: null, lng: null }),
      { timeout: 10000, enableHighAccuracy: true }
    );
  });

  const clockAction = async (action, jobId, jobName) => {
    setClockLoading(true);
    const gps = await getGPS();
    try {
      const res = await fetch(`${BASE_URL}/api/crew/time-clock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action, userId: currentUser?.id, userName: currentUser?.name,
          jobId, jobName, ...gps,
        }),
      });
      const data = await res.json();
      if (!res.ok) alert(data.error || 'Clock action failed');
      fetchClockStatus();
    } catch (e) { alert('Clock error: ' + e.message); }
    setClockLoading(false);
  };

  // ── Material flag functions ──
  const fetchOpenFlags = async () => {
    try {
      const { data, error } = await supabase
        .from('crew_material_flags')
        .select('*')
        .in('status', ['pending', 'approved'])
        .order('created_at', { ascending: false });
      if (!error && data) setOpenFlags(data);
    } catch (e) { console.log('Flag fetch error:', e.message); }
  };

  const submitMaterialFlag = async () => {
    if (!flagForm.material.trim() || !activeJob) return;
    setFlagSending(true);
    try {
      const flagRow = {
        job_id: activeJob.id,
        crew_user: currentUser?.name || 'Unknown',
        item_name: flagForm.material.trim(),
        category: flagForm.category || 'Other',
        supplier: flagForm.supplier || '',
        qty: Number(flagForm.qty) || 1,
        unit: flagForm.unit || 'ea',
        price: Number(flagForm.price) || 0,
        receipt_ref: flagForm.receiptRef || null,
        reimb: flagForm.reimb || false,
        notes: flagForm.notes ? `[${flagForm.urgency.toUpperCase()}] ${flagForm.notes}` : `[${flagForm.urgency.toUpperCase()}]`,
        status: 'pending',
        phase_target: null,
      };

      const { error } = await supabase.from('crew_material_flags').insert(flagRow);

      if (!error) {
        setFlagModal(false);
        setFlagForm({ material: '', category: 'Other', supplier: '', qty: 1, unit: 'ea', price: '', quantity: '', urgency: 'normal', notes: '', receiptRef: '', reimb: false });
        fetchOpenFlags();
        if (addNotification) addNotification("job", `📋 ${currentUser.name} flagged material needed: ${flagForm.material} for ${activeJob.project}`, "jobs");

        // Best-effort SMS notification to office
        try {
          fetch(`${BASE_URL}/api/crew/material-flag`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId: currentUser?.id, userName: currentUser?.name,
              jobId: activeJob.id, jobName: activeJob.project,
              material: flagForm.material, quantity: `${flagForm.qty} ${flagForm.unit}`,
              urgency: flagForm.urgency, notes: flagForm.notes,
            }),
          }).catch(() => {}); // fire and forget
        } catch (e) { /* SMS is best-effort */ }
      } else {
        alert('Failed to submit flag: ' + error.message);
      }
    } catch (e) { alert('Error: ' + e.message); }
    setFlagSending(false);
  };

  // ── Photo functions ──
  const handlePhotoUpload = (e, jobId) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const pending = [];
    let loaded = 0;
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        pending.push({ src: ev.target.result, name: file.name, date: new Date().toLocaleDateString() });
        loaded++;
        if (loaded === files.length) {
          setPendingPhotos(pending);
          setPhotoTagModal(true);
        }
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  // Photos are fetched lazily (the login skips them — 10 MB of base64 was making
  // the jobs request time out). Pull this job's photos as soon as it's opened.
  const openJob = (job) => {
    setActiveJob(job);
    if (job && !job.photosLoaded && loadJobPhotos) loadJobPhotos(job.id);
  };

  const confirmPhotoTag = async (tag) => {
    if (!activeJob || !pendingPhotos.length) return;
    const tagged = pendingPhotos.map(p => ({ ...p, tag, uploadedBy: currentUser?.name }));
    // The photos column is only written when photosLoaded is true, so make sure
    // this job's existing photos are in memory first — otherwise the upload is
    // dropped on save, or would overwrite the photos already on the record.
    let existing = activeJob.photos || [];
    if (!activeJob.photosLoaded && loadJobPhotos) {
      const fetched = await loadJobPhotos(activeJob.id);
      if (fetched === null) {
        if (addNotification) addNotification("job", `⚠️ Couldn't load existing photos for ${activeJob.project} — upload cancelled, please retry`, "jobs");
        return;
      }
      existing = fetched;
    }
    setJobs(prev => prev.map(j =>
      j.id === activeJob.id
        ? { ...j, photos: [...(j.photosLoaded ? (j.photos || []) : existing), ...tagged], photosLoaded: true }
        : j
    ));
    if (addNotification) addNotification("job", `📸 ${currentUser.name} uploaded ${tagged.length} "${tag}" photo(s) for ${activeJob.project}`, "jobs");
    setPendingPhotos([]);
    setPhotoTagModal(false);
  };

  // ── Notes function ──
  const addNote = (jobId) => {
    if (!noteText.trim()) return;
    const now = new Date();
    const timestamp = `[${now.toLocaleDateString()} ${now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} · ${currentUser.name}]`;
    const newNote = `${timestamp} ${noteText.trim()}`;
    setJobs(prev => prev.map(j => j.id === jobId ? { ...j, notes: j.notes ? j.notes + "\n" + newNote : newNote } : j));
    if (addNotification) addNotification("job", `📝 ${currentUser.name} added a note on ${activeJob?.project || "a job"}`, "jobs");
    setNoteText("");
  };

  // ── Status update ──
  const updateStatus = (jobId, newStatus) => {
    setJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: newStatus } : j));
    if (addNotification) {
      const emoji = newStatus === "In Progress" ? "🔨" : newStatus === "Completed" ? "✅" : "📅";
      addNotification("job", `${emoji} ${currentUser.name} marked ${activeJob?.project || "job"} as ${newStatus}`, "jobs");
    }
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      "In Progress": { bg: "rgba(59,130,246,0.12)", color: "var(--accent-blue)" },
      "Scheduled": { bg: "rgba(122,158,126,0.12)", color: "var(--accent-green)" },
      "Completed": { bg: "var(--stone-200)", color: "var(--stone-500)" },
    };
    const s = styles[status] || styles.Scheduled;
    return <span style={{ fontSize: "11px", fontWeight: 700, background: s.bg, color: s.color, padding: "4px 12px", borderRadius: 99, textTransform: "uppercase", letterSpacing: "0.04em" }}>{status}</span>;
  };

  const tagColor = (tag) => {
    const map = { Before: '#3b5bdb', After: '#1a6b2a', During: '#946c00', Substrate: '#5a5248' };
    return map[tag] || '#8c7b6e';
  };

  // ═══════════════════════════════════════════════════════════════════
  // JOB DETAIL VIEW
  // ═══════════════════════════════════════════════════════════════════
  if (activeJob) {
    const job = jobs.find(j => j.id === activeJob.id) || activeJob;
    const est = getEstimate(job.estimateId || job.estimate_id);
    const jobFlags = openFlags.filter(f => f.job_id === job.id);

    return (
      <div className="fade-in">
        <button onClick={() => setActiveJob(null)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: "var(--stone-500)", fontSize: 13, fontWeight: 600, marginBottom: 16, padding: 0 }}>
          ← Back to My Jobs
        </button>

        {/* Job Header */}
        <div className="card" style={{ padding: "20px 24px", marginBottom: 16, borderLeft: `4px solid ${job.status === "In Progress" ? "var(--accent-blue)" : job.status === "Completed" ? "var(--stone-400)" : "var(--accent-green)"}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
            <div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{job.project}</div>
              <div style={{ fontSize: 14, color: "var(--stone-500)" }}>{job.customer}</div>
            </div>
            <StatusBadge status={job.status} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, fontSize: 13 }}>
            {job.address && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span>📍</span><span style={{ fontWeight: 500 }}>{job.address}</span></div>}
            {job.startDate && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span>📅</span><span>{job.startDate}{job.endDate ? ` → ${job.endDate}` : ''}</span></div>}
            {(job.estimatedDays || 0) > 1 && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span>⏱</span><span>{job.estimatedDays} day project</span></div>}
            {(job.crewName || job.crew) && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span>👷</span><span>{job.crewName || job.crew}</span></div>}
            {/* Crew Pay — show this member's pay only */}
            {(() => {
              const crewPay = job.crewPay || job.crew_pay || [];
              const myPay = crewPay.find(p => p.name?.toLowerCase() === myName);
              if (!myPay || !myPay.amount) return null;
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span>💰</span>
                  <span style={{ fontWeight: 700, color: "var(--accent-green)" }}>Your Pay: ${Number(myPay.amount).toLocaleString()}</span>
                </div>
              );
            })()}
          </div>

          {/* Crew Role Notes */}
          {(job.crewNotes || job.crew_notes || []).length > 0 && (
            <div style={{ marginTop:12, padding:"10px 14px", background:"rgba(201,169,110,0.06)", borderRadius:"var(--radius)", border:"1px solid rgba(201,169,110,0.12)" }}>
              <div style={{ fontSize:10, fontWeight:600, color:"var(--warm-gold)", textTransform:"uppercase", letterSpacing:".04em", marginBottom:8 }}>📋 Crew Roles</div>
              <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                {(job.crewNotes || job.crew_notes || []).map((cn, ci) => (
                  <div key={ci} style={{ fontSize:13, display:"flex", gap:6, alignItems:"center" }}>
                    <span style={{ fontWeight:600, color:"var(--stone-700)", minWidth:80 }}>{cn.name?.split(" ")[0]}</span>
                    <span style={{ color:"var(--stone-300)" }}>—</span>
                    <span style={{ color:"var(--stone-600)" }}>{cn.note}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status + action buttons */}
          <div style={{ display: "flex", gap: 8, marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--stone-200)", flexWrap: "wrap" }}>
            {job.status === "Scheduled" && <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={() => updateStatus(job.id, "In Progress")}>🔨 Start Job</button>}
            {job.status === "In Progress" && <button className="btn btn-primary" style={{ background: "var(--accent-green)", borderColor: "var(--accent-green)", fontSize: 13 }} onClick={() => updateStatus(job.id, "Completed")}>✅ Mark Complete</button>}
            <button className="btn btn-secondary" style={{ fontSize: 13 }} onClick={() => setFlagModal(true)}>📋 Flag Material</button>
          </div>
        </div>

        {/* Active Material Flags */}
        {jobFlags.length > 0 && (
          <div className="card" style={{ padding: "14px 20px", marginBottom: 16, borderLeft: "4px solid var(--accent-amber)" }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "var(--accent-amber)", marginBottom: 8 }}>⚠️ Open Material Flags ({jobFlags.length})</div>
            {jobFlags.map(f => {
              const urgency = (f.notes || '').match(/\[(NORMAL|URGENT|CRITICAL)\]/)?.[1]?.toLowerCase() || 'normal';
              return (
              <div key={f.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--stone-100)", fontSize: 13 }}>
                <div>
                  <span style={{ fontWeight: 600 }}>{f.item_name}</span>
                  {f.qty && <span style={{ color: "var(--stone-400)", marginLeft: 6 }}>({f.qty} {f.unit})</span>}
                  {f.supplier && <span style={{ color: "var(--stone-400)", marginLeft: 6 }}>· {f.supplier}</span>}
                  {f.price > 0 && <span style={{ color: "var(--warm-gold)", marginLeft: 6 }}>· ${Number(f.price).toFixed(2)}</span>}
                  {f.notes && <div style={{ fontSize: 11, color: "var(--stone-400)", marginTop: 2 }}>{f.notes}</div>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 4,
                    background: f.status === 'approved' ? 'rgba(122,158,126,.15)' : 'rgba(201,169,110,.15)',
                    color: f.status === 'approved' ? 'var(--accent-green)' : 'var(--warm-gold)',
                  }}>{f.status}</span>
                  <span style={{
                    fontSize: 10, fontWeight: 700, textTransform: "uppercase", padding: "3px 8px", borderRadius: 4,
                    background: urgency === 'critical' ? '#c92a2a18' : urgency === 'urgent' ? '#946c0018' : '#5a524818',
                    color: urgency === 'critical' ? '#c92a2a' : urgency === 'urgent' ? '#946c00' : '#5a5248',
                  }}>{urgency}</span>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* Scope of Work (no pricing) */}
        {est && est.items && (
          <div className="card" style={{ padding: "18px 22px", marginBottom: 16 }}>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, marginBottom: 12 }}>📋 Scope of Work</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {est.items.map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "var(--stone-50)", borderRadius: "var(--radius)", borderLeft: "3px solid var(--warm-gold)" }}>
                  <span style={{ fontSize: 14, color: "var(--accent-green)" }}>✓</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{item.desc}</div>
                    <div style={{ fontSize: 12, color: "var(--stone-400)" }}>{item.qty} {item.unit}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Progress Photos with Tags */}
        <div className="card" style={{ padding: "18px 22px", marginBottom: 16 }}>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>📸 Progress Photos ({photoLoading?.[job.id] ? "…" : (job.photos || []).length})</span>
            <button className="btn btn-sm" style={{ background: "var(--warm-gold)", color: "white", border: "none", fontSize: 12 }} onClick={() => fileRef.current?.click()}>
              + Upload Photo
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={e => handlePhotoUpload(e, job.id)} />
          </div>

          {photoLoading?.[job.id] ? (
            <div style={{ padding: "28px", textAlign: "center", fontSize: 13, color: "var(--stone-500)" }}>Loading photos…</div>
          ) : (job.photos || []).length === 0 ? (
            <div onClick={() => fileRef.current?.click()} style={{ padding: "28px", textAlign: "center", border: "2px dashed var(--stone-300)", borderRadius: "var(--radius)", cursor: "pointer", color: "var(--stone-400)", fontSize: 13 }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>📷</div>
              Tap to upload progress photos
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(100px,1fr))", gap: 8 }}>
              {(job.photos || []).map((p, i) => (
                <div key={i} style={{ aspectRatio: "1", borderRadius: 10, overflow: "hidden", border: p.tag === 'After' ? "2px solid #1a6b2a" : p.tag === 'Before' ? "2px solid #3b5bdb" : "1px solid var(--stone-200)", position: "relative" }}>
                  <img src={p.src || p} alt={p.name || ''} style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }} onClick={() => setLightbox(p)} />
                  {p.tag && (
                    <div style={{ position: "absolute", top: 3, left: 3, background: tagColor(p.tag), color: "white", fontSize: "8px", fontWeight: 700, padding: "2px 5px", borderRadius: 3, textTransform: "uppercase" }}>
                      {p.tag}
                    </div>
                  )}
                </div>
              ))}
              <div onClick={() => fileRef.current?.click()} style={{ aspectRatio: "1", borderRadius: 10, border: "2px dashed var(--stone-300)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--stone-400)", fontSize: 24 }}>+</div>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="card" style={{ padding: "18px 22px" }}>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, marginBottom: 12 }}>📝 Job Notes & Updates</div>
          {job.notes ? (
            <div style={{ marginBottom: 14, maxHeight: 300, overflowY: "auto" }}>
              {job.notes.split("\n").filter(Boolean).map((line, i) => {
                const isTimestamped = line.startsWith("[");
                return (
                  <div key={i} style={{ padding: "8px 12px", marginBottom: 4, borderRadius: "var(--radius)", background: isTimestamped ? "rgba(201,169,110,0.06)" : "var(--stone-50)", borderLeft: isTimestamped ? "3px solid var(--warm-gold)" : "3px solid var(--stone-200)", fontSize: 13, lineHeight: 1.6, color: "var(--stone-700)" }}>
                    {line}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: "16px", textAlign: "center", color: "var(--stone-400)", fontSize: 13, marginBottom: 14 }}>No notes yet — add your first update below.</div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={2}
              placeholder="Add a progress update, issue, or note…"
              style={{ flex: 1, padding: "10px 14px", border: "1.5px solid var(--stone-300)", borderRadius: "var(--radius)", fontSize: 13, resize: "vertical" }}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addNote(job.id); } }}
            />
            <button onClick={() => addNote(job.id)} disabled={!noteText.trim()}
              style={{ padding: "10px 16px", background: noteText.trim() ? "var(--warm-gold)" : "var(--stone-200)", color: "white", border: "none", borderRadius: "var(--radius)", cursor: noteText.trim() ? "pointer" : "default", fontWeight: 600, fontSize: 13, alignSelf: "flex-end" }}>
              Add
            </button>
          </div>
        </div>

        {/* ── Photo Tag Picker Modal ── */}
        {photoTagModal && (
          <div className="modal-overlay" onClick={() => { setPhotoTagModal(false); setPendingPhotos([]); }} style={{ zIndex: 2001 }}>
            <div className="modal-box fade-in" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
              <h2 style={{ fontFamily: "var(--font-display)", fontSize: "18px", fontWeight: 600, marginBottom: 16 }}>
                Tag {pendingPhotos.length} Photo{pendingPhotos.length !== 1 ? "s" : ""}
              </h2>
              <div style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto" }}>
                {pendingPhotos.slice(0, 6).map((p, i) => (
                  <img key={i} src={p.src} alt="" style={{ width: 56, height: 56, borderRadius: 8, objectFit: "cover", border: "2px solid var(--stone-200)" }} />
                ))}
                {pendingPhotos.length > 6 && <div style={{ width: 56, height: 56, borderRadius: 8, background: "var(--stone-200)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "var(--stone-500)" }}>+{pendingPhotos.length - 6}</div>}
              </div>
              <div style={{ fontSize: 13, color: "var(--stone-500)", marginBottom: 12 }}>What stage are these photos from?</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {PHOTO_TAGS.map(tag => (
                  <button key={tag.id} onClick={() => confirmPhotoTag(tag.id)} style={{
                    display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
                    borderRadius: 10, border: "1px solid var(--stone-200)", background: "white",
                    cursor: "pointer", textAlign: "left",
                  }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: tag.color, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: "var(--stone-800)" }}>{tag.label}</div>
                      <div style={{ fontSize: 11, color: "var(--stone-400)" }}>{tag.desc}{tag.id === "After" ? " ✦" : ""}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Material Flag Modal ── */}
        {flagModal && (
          <div className="modal-overlay" onClick={() => setFlagModal(false)} style={{ zIndex: 2001 }}>
            <div className="modal-box fade-in" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
              <h2 style={{ fontFamily: "var(--font-display)", fontSize: "18px", fontWeight: 600, marginBottom: 4 }}>Flag Material Purchase</h2>
              <div style={{ fontSize: 12, color: "var(--stone-400)", marginBottom: 16 }}>This writes directly to the project budget. Office will be notified.</div>

              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "var(--stone-600)", marginBottom: 4, display: "block" }}>Item Name *</label>
                  <input value={flagForm.material} onChange={e => setFlagForm(p => ({ ...p, material: e.target.value }))}
                    placeholder="e.g., Schluter Kerdi Board, thinset mortar..."
                    style={{ width: "100%", padding: "10px 14px", border: "1.5px solid var(--stone-300)", borderRadius: "var(--radius)", fontSize: 14 }} />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--stone-600)", marginBottom: 4, display: "block" }}>Category</label>
                    <select value={flagForm.category} onChange={e => setFlagForm(p => ({ ...p, category: e.target.value }))}
                      style={{ width: "100%", padding: "10px", border: "1.5px solid var(--stone-300)", borderRadius: "var(--radius)", fontSize: 13 }}>
                      {FLAG_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--stone-600)", marginBottom: 4, display: "block" }}>Supplier</label>
                    <select value={flagForm.supplier} onChange={e => setFlagForm(p => ({ ...p, supplier: e.target.value }))}
                      style={{ width: "100%", padding: "10px", border: "1.5px solid var(--stone-300)", borderRadius: "var(--radius)", fontSize: 13 }}>
                      <option value="">Select...</option>
                      {FLAG_SUPPLIERS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--stone-600)", marginBottom: 4, display: "block" }}>Qty *</label>
                    <input type="number" min="1" value={flagForm.qty} onChange={e => setFlagForm(p => ({ ...p, qty: e.target.value }))}
                      style={{ width: "100%", padding: "10px 14px", border: "1.5px solid var(--stone-300)", borderRadius: "var(--radius)", fontSize: 14 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--stone-600)", marginBottom: 4, display: "block" }}>Unit</label>
                    <select value={flagForm.unit} onChange={e => setFlagForm(p => ({ ...p, unit: e.target.value }))}
                      style={{ width: "100%", padding: "10px", border: "1.5px solid var(--stone-300)", borderRadius: "var(--radius)", fontSize: 13 }}>
                      {FLAG_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--stone-600)", marginBottom: 4, display: "block" }}>Unit Price ($)</label>
                    <input type="number" step="0.01" min="0" value={flagForm.price} onChange={e => setFlagForm(p => ({ ...p, price: e.target.value }))}
                      placeholder="0.00"
                      style={{ width: "100%", padding: "10px 14px", border: "1.5px solid var(--stone-300)", borderRadius: "var(--radius)", fontSize: 14 }} />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "var(--stone-600)", marginBottom: 4, display: "block" }}>Receipt / Reference #</label>
                  <input value={flagForm.receiptRef} onChange={e => setFlagForm(p => ({ ...p, receiptRef: e.target.value }))}
                    placeholder="Optional — receipt number or store order #"
                    style={{ width: "100%", padding: "10px 14px", border: "1.5px solid var(--stone-300)", borderRadius: "var(--radius)", fontSize: 13 }} />
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "var(--stone-600)", marginBottom: 4, display: "block" }}>Urgency</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    {[
                      { id: 'normal', label: 'Normal', color: 'var(--stone-500)' },
                      { id: 'urgent', label: 'Urgent', color: '#946c00' },
                      { id: 'critical', label: 'Critical', color: '#c92a2a' },
                    ].map(u => (
                      <button key={u.id} onClick={() => setFlagForm(p => ({ ...p, urgency: u.id }))} style={{
                        flex: 1, padding: "10px", borderRadius: "var(--radius)", fontSize: 13, fontWeight: 600, cursor: "pointer",
                        border: flagForm.urgency === u.id ? `2px solid ${u.color}` : "1.5px solid var(--stone-200)",
                        background: flagForm.urgency === u.id ? u.color + '10' : "white",
                        color: flagForm.urgency === u.id ? u.color : "var(--stone-500)",
                      }}>{u.label}</button>
                    ))}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--stone-600)", marginBottom: 4, display: "block" }}>Notes</label>
                    <textarea value={flagForm.notes} onChange={e => setFlagForm(p => ({ ...p, notes: e.target.value }))}
                      placeholder="Any details — specific color, brand, where to deliver..."
                      rows={2} style={{ width: "100%", padding: "10px 14px", border: "1.5px solid var(--stone-300)", borderRadius: "var(--radius)", fontSize: 13, resize: "vertical" }} />
                  </div>
                </div>

                <div onClick={() => setFlagForm(p => ({ ...p, reimb: !p.reimb }))}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: "var(--radius)", border: flagForm.reimb ? "1.5px solid var(--accent-amber)" : "1.5px solid var(--stone-200)", background: flagForm.reimb ? "rgba(196,149,74,.06)" : "white", cursor: "pointer" }}>
                  <div style={{ width: 20, height: 20, borderRadius: 4, border: flagForm.reimb ? "2px solid var(--accent-amber)" : "2px solid var(--stone-300)", background: flagForm.reimb ? "var(--accent-amber)" : "white", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 12, fontWeight: 700 }}>
                    {flagForm.reimb ? "✓" : ""}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--stone-700)" }}>Paid out of pocket</div>
                    <div style={{ fontSize: 11, color: "var(--stone-400)" }}>Check if you personally paid — flags for reimbursement</div>
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
                <button className="btn btn-secondary" onClick={() => setFlagModal(false)}>Cancel</button>
                <button className="btn btn-primary" disabled={!flagForm.material.trim() || flagSending} onClick={submitMaterialFlag}>
                  {flagSending ? 'Sending...' : '📋 Submit Flag'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Lightbox ── */}
        {lightbox && (
          <div className="modal-overlay" onClick={() => setLightbox(null)} style={{ zIndex: 2000 }}>
            <div style={{ background: "var(--stone-950)", borderRadius: 16, overflow: "hidden", maxWidth: "80vw", maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }} onClick={e => e.stopPropagation()}>
              <img src={lightbox.src || lightbox} alt="" style={{ maxWidth: "80vw", maxHeight: "75vh", objectFit: "contain", display: "block" }} />
              <div style={{ padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ color: "var(--stone-200)", fontSize: 13, fontWeight: 500 }}>{lightbox.name || ''}</div>
                  <div style={{ color: "var(--stone-500)", fontSize: 11 }}>{lightbox.tag && <span style={{ color: tagColor(lightbox.tag), fontWeight: 600 }}>{lightbox.tag}</span>} {lightbox.date || ''}</div>
                </div>
                <button className="btn btn-ghost btn-sm" style={{ color: "var(--stone-400)" }} onClick={() => setLightbox(null)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // JOB LIST VIEW (main screen)
  // ═══════════════════════════════════════════════════════════════════
  const jobList = tab === "active" ? active : tab === "upcoming" ? upcoming : completed;

  return (
    <div className="fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 className="section-title">{isManager ? 'Crew App' : 'My Jobs'}</h1>
          <p className="section-sub">{isManager ? 'All crew jobs — material flags, photo uploads, and progress tracking.' : 'Your assigned projects — tap a job to view details, upload photos, or add notes.'}</p>
        </div>
        {/* Time Clock Toggle — owner/admin can enable, stored in localStorage */}
        {(currentUser?.role === 'owner' || currentUser?.role === 'admin' || timeClockEnabled) && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {(currentUser?.role === 'owner' || currentUser?.role === 'admin') && (
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--stone-400)", cursor: "pointer" }}>
                <input type="checkbox" checked={timeClockEnabled} onChange={e => setTimeClockEnabled(e.target.checked)}
                  style={{ accentColor: "var(--warm-gold)" }} />
                Time Clock
              </label>
            )}
          </div>
        )}
      </div>

      {/* ── Time Clock Bar (togglable) ── */}
      {timeClockEnabled && (
        <div className="card" style={{ padding: "16px 20px", marginBottom: 16, borderLeft: clockStatus?.clockedIn ? "4px solid var(--accent-green)" : "4px solid var(--stone-300)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>
                {clockStatus?.clockedIn
                  ? clockStatus?.onBreak ? '☕ On Break' : '🔨 Clocked In'
                  : '⏸ Not Clocked In'}
              </div>
              {clockStatus?.active && (
                <div style={{ fontSize: 12, color: "var(--stone-400)" }}>
                  Since {new Date(clockStatus.active.clock_in).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  {clockStatus.active.job_name && ` · ${clockStatus.active.job_name}`}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {!clockStatus?.clockedIn ? (
                <button className="btn btn-primary btn-sm" disabled={clockLoading}
                  onClick={() => {
                    const topJob = active[0] || upcoming[0];
                    clockAction('clock-in', topJob?.id, topJob?.project);
                  }}>
                  {clockLoading ? '...' : '⏱ Clock In'}
                </button>
              ) : (
                <>
                  {!clockStatus.onBreak ? (
                    <button className="btn btn-secondary btn-sm" disabled={clockLoading}
                      onClick={() => clockAction('break-start')}>
                      ☕ Break
                    </button>
                  ) : (
                    <button className="btn btn-secondary btn-sm" disabled={clockLoading}
                      onClick={() => clockAction('break-end')}>
                      ▶ End Break
                    </button>
                  )}
                  <button className="btn btn-sm" disabled={clockLoading}
                    style={{ background: "#c92a2a", color: "white", border: "none" }}
                    onClick={() => clockAction('clock-out')}>
                    ⏹ Clock Out
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12, marginBottom: 24 }}>
        {[
          ["Active", active.length, "var(--accent-blue)"],
          ["Upcoming", upcoming.length, "var(--accent-green)"],
          ["This Week", thisWeek.length, "var(--warm-gold)"],
          ["Completed", completed.length, "var(--stone-400)"],
        ].map(([l, v, c]) => (
          <div key={l} className="stat-card">
            <div className="stat-value" style={{ color: c }}>{v}</div>
            <div className="stat-label">{l}</div>
          </div>
        ))}
      </div>

      {/* Open Material Flags Banner */}
      {openFlags.length > 0 && (
        <div className="card" style={{ padding: "12px 20px", marginBottom: 16, borderLeft: "4px solid var(--accent-amber)", background: "rgba(148,108,0,0.04)" }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: "var(--accent-amber)" }}>
            ⚠️ {openFlags.length} open material flag{openFlags.length !== 1 ? 's' : ''} — office has been notified
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: "4px", background: "var(--stone-200)", borderRadius: "var(--radius)", padding: "3px", marginBottom: 20 }}>
        {[
          { id: "active", label: "Active", count: active.length },
          { id: "upcoming", label: "Upcoming", count: upcoming.length },
          { id: "completed", label: "Completed", count: completed.length },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex: 1, padding: "8px 14px", borderRadius: 6, border: "none", cursor: "pointer",
            background: tab === t.id ? "white" : "transparent",
            color: tab === t.id ? "var(--stone-900)" : "var(--stone-500)",
            fontWeight: tab === t.id ? 700 : 500, fontSize: 13,
            boxShadow: tab === t.id ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
          }}>
            {t.label}
            {t.count > 0 && <span style={{ marginLeft: 6, background: tab === t.id ? "var(--warm-gold)" : "var(--stone-300)", color: "white", fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 99 }}>{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Job Cards */}
      {jobList.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>{tab === "active" ? "🔨" : tab === "upcoming" ? "📅" : "✅"}</div>
          <div style={{ fontWeight: 600, color: "var(--stone-500)" }}>No {tab} jobs</div>
          <div style={{ fontSize: 13, color: "var(--stone-400)", marginTop: 4 }}>
            {tab === "active" ? "No jobs currently in progress." : tab === "upcoming" ? "No upcoming jobs scheduled." : "No completed jobs yet."}
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {jobList.map(job => {
            const jobFlagCount = openFlags.filter(f => f.job_id === job.id).length;
            return (
              <div key={job.id} onClick={() => openJob(job)} className="card" style={{
                padding: "16px 20px", cursor: "pointer", transition: "all 0.15s",
                borderLeft: `4px solid ${job.status === "In Progress" ? "var(--accent-blue)" : job.status === "Completed" ? "var(--stone-300)" : "var(--accent-green)"}`
              }}
                onMouseEnter={e => e.currentTarget.style.transform = "translateX(4px)"}
                onMouseLeave={e => e.currentTarget.style.transform = "none"}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{job.project}</div>
                    <div style={{ fontSize: 13, color: "var(--stone-500)", marginBottom: 6 }}>{job.customer}</div>
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "var(--stone-400)" }}>
                      {job.address && <span>📍 {job.address}</span>}
                      {job.startDate && <span>📅 {job.startDate}{job.endDate && job.endDate !== job.startDate ? ` → ${job.endDate}` : ''}</span>}
                      {(() => {
                        const cp = job.crewPay || job.crew_pay || [];
                        const my = cp.find(p => p.name?.toLowerCase() === myName);
                        return my?.amount ? <span style={{ color: "var(--accent-green)", fontWeight: 600 }}>💰 ${Number(my.amount).toLocaleString()}</span> : null;
                      })()}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                    <StatusBadge status={job.status} />
                    <div style={{ display: "flex", gap: 8, fontSize: 11, color: "var(--stone-400)" }}>
                      {(job.photos || []).length > 0 && <span>📸 {job.photos.length}</span>}
                      {jobFlagCount > 0 && <span style={{ color: "var(--accent-amber)" }}>⚠️ {jobFlagCount}</span>}
                      {job.notes && <span>📝</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CrewApp;
